import { Capacitor } from '@capacitor/core';
import { SfmcMobilePush } from 'capacitor-sfmc-mobile-push';

import { getConfig } from '../../../config/environment';

export const PUSH_NOTIFICATIONS_OPT_OUT_KEY = 'push_notifications_opted_out';
export const PUSH_NOTIFICATIONS_CONSENTED_KEY = 'push_notifications_consented';

let sfmcInitialized = false;

/**
 * Initializes the SFMC Mobile Messaging SDK. Idempotent — safe to call from multiple
 * places (app startup, before requestPermissions()). No-op on web.
 *
 * Config values come from Setup → Mobile Push → App (per platform) in Marketing Cloud —
 * see Part 0.2.1/0.2.2 of the push-notifications plan. Until that org is provisioned,
 * these env vars are unset and initialize() is skipped.
 */
export const initializeSfmcSdk = async (): Promise<void> => {
  if (!Capacitor.isNativePlatform() || sfmcInitialized) return;

  const config = getConfig();
  const isIos = Capacitor.getPlatform() === 'ios';
  const appId = isIos ? config.sfmcAppIdIos : config.sfmcAppIdAndroid;
  const accessToken = isIos ? config.sfmcAccessTokenIos : config.sfmcAccessTokenAndroid;
  const serverUrl = isIos ? config.sfmcServerUrlIos : config.sfmcServerUrlAndroid;
  const mid = config.sfmcMid;

  if (!appId || !accessToken || !serverUrl || !mid) {
    console.warn('[SfmcMobilePush] Marketing Cloud MobilePush app not configured — skipping SDK init.');
    return;
  }

  try {
    await SfmcMobilePush.initialize({ appId, accessToken, serverUrl, mid });
    sfmcInitialized = true;
  } catch (error) {
    console.warn('[SfmcMobilePush] initialize() failed:', error);
  }
};

export const requestPushNotificationPermission = async (): Promise<void> => {
  if (!Capacitor.isNativePlatform()) return;

  const { receive } = await SfmcMobilePush.requestPermissions();
  if (receive === 'granted') {
    localStorage.setItem(PUSH_NOTIFICATIONS_CONSENTED_KEY, 'true');
    localStorage.removeItem(PUSH_NOTIFICATIONS_OPT_OUT_KEY);
    // OS permission alone doesn't tell Marketing Cloud the device is opted in — without this,
    // devices register but sit as "Not Opted In" in SFMC forever. Never let a native failure
    // here block onboarding (callers await this inside Promise.all with no catch).
    try {
      await initializeSfmcSdk();
      await SfmcMobilePush.enablePush();
    } catch (error) {
      console.warn('[SfmcMobilePush] enablePush() failed:', error);
    }
  } else {
    localStorage.setItem(PUSH_NOTIFICATIONS_CONSENTED_KEY, 'false');
    localStorage.removeItem(PUSH_NOTIFICATIONS_OPT_OUT_KEY);
  }
};

export const skipPushNotifications = (): void => {
  localStorage.setItem(PUSH_NOTIFICATIONS_OPT_OUT_KEY, 'true');
  localStorage.removeItem(PUSH_NOTIFICATIONS_CONSENTED_KEY);
};

/**
 * Returns `true` when push notifications should be treated as unavailable.
 *
 * Evaluation order:
 * 1. Opted-out via `skipPushNotifications` → `true` immediately.
 * 2. On native: `SfmcMobilePush.checkPermissions()` is called regardless of the
 *    app-level consent flag — `true` if the OS permission is not `granted`, or if
 *    the consent flag is also not set.
 * 3. On web (non-native): falls back to the consent flag alone (`true` when not consented).
 *
 * `false` only when both the OS permission and the app consent flag are confirmed granted.
 */
export const pushNotificationIsSkippedOrNotAllowed = async (): Promise<boolean> => {
  const optedOut = localStorage.getItem(PUSH_NOTIFICATIONS_OPT_OUT_KEY) === 'true';
  const consented = localStorage.getItem(PUSH_NOTIFICATIONS_CONSENTED_KEY) === 'true';

  if (optedOut) return true;

  if (!Capacitor.isNativePlatform()) return !consented;

  const { receive } = await SfmcMobilePush.checkPermissions();
  return receive !== 'granted';
};

// Requires Firebase on Android (google-services.json) and an APNs key/cert registered with
// Marketing Cloud's iOS MobilePush app (no GoogleService-Info.plist needed on iOS — SFMC uses
// APNs directly there). Call at app startup, after initializeSfmcSdk().
export const registerPushNotificationsIfConsented = async (): Promise<void> => {
  if (!Capacitor.isNativePlatform()) return;

  const optedOut = localStorage.getItem(PUSH_NOTIFICATIONS_OPT_OUT_KEY) === 'true';
  const consented = localStorage.getItem(PUSH_NOTIFICATIONS_CONSENTED_KEY) === 'true';

  if (optedOut || !consented) return;

  try {
    await initializeSfmcSdk();
    await SfmcMobilePush.enablePush();
  } catch (error) {
    console.warn('[SfmcMobilePush] registerPushNotificationsIfConsented() failed:', error);
  }
};

/**
 * Re-checks the OS permission against our app-level consent flag and resyncs
 * enablePush()/disablePush() accordingly — closes the gap where a user toggles notification
 * permission in the OS Settings app directly (not through our in-app UI) and nothing here
 * would otherwise notice. Never prompts the OS dialog (uses checkPermissions(), not
 * requestPermissions()) — this is a passive resync, not a fresh permission request.
 * Skips entirely if the user explicitly opted out via skipPushNotifications(), so we never
 * silently re-enable behind their back.
 */
export const resyncPushPermissionState = async (): Promise<void> => {
  if (!Capacitor.isNativePlatform()) return;

  const optedOut = localStorage.getItem(PUSH_NOTIFICATIONS_OPT_OUT_KEY) === 'true';
  if (optedOut) return;

  try {
    const { receive } = await SfmcMobilePush.checkPermissions();
    if (receive === 'granted') {
      localStorage.setItem(PUSH_NOTIFICATIONS_CONSENTED_KEY, 'true');
      await initializeSfmcSdk();
      await SfmcMobilePush.enablePush();
    } else if (localStorage.getItem(PUSH_NOTIFICATIONS_CONSENTED_KEY) === 'true') {
      // Only actively disable if we'd previously considered this device consented/enabled —
      // avoids calling disablePush() on every resume for someone who never granted at all.
      localStorage.setItem(PUSH_NOTIFICATIONS_CONSENTED_KEY, 'false');
      await SfmcMobilePush.disablePush();
    }
  } catch (error) {
    console.warn('[SfmcMobilePush] resyncPushPermissionState() failed:', error);
  }
};
